import { computed, onBeforeMount, onMounted } from 'vue-demi';
import { useCustomerMgmtStore } from './customer-mgmt-store';
import type { SelectWithSearchOptions, FetchListOptions } from './types/types';

const useCustomerMgmt = (options: SelectWithSearchOptions) => {
  const customerMgmtStore = useCustomerMgmtStore(options.featureKey);
  // The order does matter, in case of changing order sorting will be required.
  const defaultFetchParams: FetchListOptions = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ['id'],
    sortDesc: [false],
    groupBy: ['id'],
    groupDesc: [false],
    mustSort: false,
    multiSort: false,
    searchString: '',
    filters: {},
    force: false,
  };

  const fetchParams = computed(() => customerMgmtStore.getFetchParams(options.context) || defaultFetchParams);
  const searchField = computed(() => customerMgmtStore.getSearchField(options.context));
  const selectedItems = computed(() => customerMgmtStore.getSelectedItems(options.context));
  const displayedItems = computed(() => customerMgmtStore.getDisplayedItems(options.context));
  const isFetching = computed(() => customerMgmtStore.getIsFetching(options.context));
  const hasMore = computed(() => customerMgmtStore.getHasMore(options.context));
  const totalItems = computed(() => customerMgmtStore.getTotalItems(options.context) || 0);

  const setFetchParams = (newFetchParams: FetchListOptions) =>
    customerMgmtStore.setFetchParams({ context: options.context, newFetchParams });
  const setSearchField = (value: string) =>
    customerMgmtStore.setSearchField({ context: options.context, newSearchField: value });
  const setSelectedItems = (items: unknown | unknown[]) =>
    customerMgmtStore.setSelectedItems({ context: options.context, items });
  const cleanAndFetch = () => customerMgmtStore.cleanAndFetch(options);
  const fetchSearch = () => customerMgmtStore.fetchSearch(options);
  const fetchPreSelected = () => customerMgmtStore.fetchPreSelected(options);
  const infiniteScrollIntersect = (entries: unknown, observer: unknown, isIntersecting: boolean) =>
    customerMgmtStore.infiniteScrollIntersect(options, entries, observer, isIntersecting);

  const getItemByUid = (uid: string) => customerMgmtStore.getItemByUid(options.context, uid);
  const resetToDefaults = () => customerMgmtStore.resetToDefaults(options.context);

  onBeforeMount(async () => {
    if (fetchParams.value === null) {
      const fetchPrams = options.options !== undefined ? options.options : defaultFetchParams;
      setFetchParams(fetchPrams);
    }
  });

  onMounted(async () => {
    await fetchPreSelected();
  });

  return {
    fetchParams,
    searchField,
    selectedItems,
    displayedItems,
    isFetching,
    hasMore,
    totalItems,
    setFetchParams,
    setSearchField,
    setSelectedItems,
    cleanAndFetch,
    fetchSearch,
    infiniteScrollIntersect,
    getItemByUid,
    resetToDefaults,
  };
};

export default useCustomerMgmt;
